// Public barrel for the design-system layer.
// Only stable, ready-for-consumption exports go here.
//
// Note: component `*Props` types are intentionally NOT re-exported here — consumers infer props from
// usage (JSX), and each `*Props` stays in its component folder. Only non-Props type unions
// (`IconName`, `MascotName`, `TabKey`) and the domain `UI*` shapes are exported as types.
export * from "./theme"

// Typography atoms
export { Text } from "./atoms/typography/Text"
export { Title } from "./atoms/typography/Title"
export { Paragraph } from "./atoms/typography/Paragraph"

// Container atoms
export { Row } from "./atoms/containers/Row"
export { Stack } from "./atoms/containers/Stack"

// Icon atoms
export { Icon } from "./atoms/icons/Icon"
export type { IconName } from "./atoms/icons/Icon"
export { ImageIcon } from "./atoms/icons/ImageIcon"
export { IconBubble } from "./atoms/icons/IconBubble"

// Input atoms
export { TextInput } from "./atoms/TextInput"

// Button atoms
export { IconButton } from "./atoms/Buttons/IconButton"
export { LabeledIconButton } from "./atoms/Buttons/LabeledIconButton"
export { Button } from "./atoms/Buttons/Button"
export { TextButton } from "./atoms/Buttons/TextButton"
export { TabBarButton } from "./atoms/Buttons/TabBarButton"
export { DashedButton } from "./atoms/DashedButton"

// Feedback / indicator atoms
export { Avatar } from "./atoms/Avatar"
export { Badge } from "./atoms/Badge"
export { Checkbox } from "./atoms/Checkbox"
export { SubjectBadge } from "./atoms/SubjectBadge"
export { ProgressBar } from "./atoms/ProgressBar"
export { FlagProgressBar } from "./atoms/FlagProgressBar"
export { CircularProgress } from "./atoms/CircularProgress"
export { ChapterBadge } from "./atoms/ChapterBadge"
export { Banner } from "./atoms/Banner"
export { Divider } from "./atoms/Divider"
export { FocusRing } from "./atoms/FocusRing"
export { Mascot } from "./atoms/Mascot"
export type { MascotName } from "./atoms/Mascot"
export { Satisfaction } from "./atoms/Satisfaction"
export { SegmentedBar } from "./atoms/SegmentedBar"
export { MediaThumbnail } from "./atoms/MediaThumbnail"
export { HorizontalList } from "./atoms/HorizontalList"
export { NotificationDot } from "./atoms/NotificationDot"

// Overlay primitive (portal-backed scrim shell reused by Modal / ActionSheet / ConfirmDialog)
export { Overlay } from "./atoms/Overlay"

// Gesture primitive (reanimated pinch/pan/rotate container reused by ImageDisplay)
export { PinchZoom } from "./atoms/PinchZoom"

// Atomes partagés des parcours parent
export { Switch } from "./atoms/Switch"
export { Highlight } from "./atoms/Highlight"
export { DelayedSkeleton, Skeleton, SkeletonGroup } from "./atoms/Skeleton"

// Molecules
export { BottomTab, TABS_BY_TYPE } from "./molecules/BottomTab"
export type { TabKey, TabType } from "./molecules/BottomTab"
export { Loader } from "./atoms/Loader"
export { Accordion } from "./molecules/Accordion"
export { Card } from "./molecules/Card"
export { HighlightFrame } from "./molecules/HighlightFrame"
export { SubjectBadges } from "./molecules/SubjectBadges"
export { ThreeDotMenu } from "./molecules/ThreeDotMenu"
export { ActionSheet } from "./molecules/ActionSheet"
export { ConfirmDialog } from "./molecules/ConfirmDialog"
export { ImageDisplay } from "./molecules/ImageDisplay"
export { PdfDisplay } from "./molecules/PdfDisplay"
export { LinkDisplay } from "./molecules/LinkDisplay"
export { ImageAttachment } from "./molecules/ImageAttachment"
export { PDFAttachment } from "./molecules/PDFAttachment"
export { LinkAttachment } from "./molecules/LinkAttachment"
export { List } from "./molecules/List"
export { InfiniteList } from "./molecules/InfiniteList"
export { Header } from "./molecules/Header"
export { Modal } from "./molecules/Modal"
export { HorizontalSelectInput } from "./molecules/HorizontalSelectInput"
export { Autocomplete } from "./molecules/Autocomplete"
export { DatePicker } from "./molecules/DatePicker"
export { BulletList } from "./molecules/BulletList"
export { RecapList } from "./molecules/RecapList"
export { ReportHero } from "./molecules/ReportHero"
export { ReportSamCta } from "./molecules/ReportSamCta"
export { BilanCard } from "./molecules/BilanCard"
export { AlertCard } from "./molecules/AlertCard"
export { SatisfactionCard } from "./molecules/SatisfactionCard"
export { LiveEngagementScore } from "./molecules/LiveEngagementScore"
export { LiveEngagementScoreList } from "./molecules/LiveEngagementScoreList"
export { LiveEngagementScoreAlerts } from "./molecules/LiveEngagementScoreAlerts"
export { EngagementGauge } from "./molecules/EngagementGauge"
export { SchoolAssessmentCard } from "./molecules/SchoolAssessmentCard"
export { SchoolAssessmentDetail } from "./molecules/SchoolAssessmentDetail"
export { SchoolAssessmentAIPreview } from "./molecules/SchoolAssessmentAIPreview"
export { ReportChapterRow } from "./molecules/ReportChapterRow"
export { ReportAssessmentHeader } from "./molecules/ReportAssessmentHeader"
export { SessionActivityRow } from "./molecules/SessionActivityRow"
export { NpsScoreScale } from "./molecules/NpsScoreScale"
export { ReferralShareHeader } from "./molecules/ReferralShareHeader"
export { ReferralLinkRow } from "./molecules/ReferralLinkRow"
export { ReferralShareActions } from "./molecules/ReferralShareActions"
export { ReferralBenefitBanner } from "./molecules/ReferralBenefitBanner"
export { ProgramMixBar } from "./molecules/ProgramMixBar"
export { RecomposeSteps } from "./molecules/RecomposeSteps"
export { AdaptedRequestBanner } from "./molecules/AdaptedRequestBanner"
export { ReportConceptRow } from "./molecules/ReportConceptRow"
export { ReportTimeCard } from "./molecules/ReportTimeCard"
export { ObjectiveHoursChip } from "./molecules/ObjectiveHoursChip"
export { LiveSessionMenu } from "./molecules/LiveSessionMenu"
export { LiveStatusBadges } from "./molecules/LiveStatusBadges"
export { QuickActionTile } from "./molecules/QuickActionTile"
export { QuickActionRow } from "./molecules/QuickActionRow"
export { TodoRow } from "./molecules/TodoRow"
export { SessionRow } from "./molecules/SessionRow"
export { LateMinutesPicker } from "./molecules/LateMinutesPicker"
export { FlowOutcome } from "./molecules/FlowOutcome"
export { Glow } from "./molecules/Glow"
export { AuroraFill } from "./molecules/AuroraFill"

// Molecules partagées des parcours parent (choix, réglages, étapes, récapitulatifs, accusés)
export { OptionCard } from "./molecules/OptionCard"
export { SettingRow } from "./molecules/SettingRow"
export { SubscriptionLine } from "./molecules/SubscriptionLine"
export { SubscriptionNoticeCard } from "./molecules/SubscriptionNoticeCard"
export { ProfilePill } from "./molecules/ProfilePill"
export { ChildChip } from "./molecules/ChildChip"
export { ChildChips } from "./molecules/ChildChips"
export { HelpBubble } from "./molecules/HelpBubble"
export { ImpersonationBanner } from "./molecules/ImpersonationBanner"
export { Snackbar } from "./molecules/Snackbar"
export { SummaryRow } from "./molecules/SummaryRow"

// Organisms — domain-aware cards + self-contained sections
export { LiveSessionCard } from "./organisms/LiveSessionCard"
export type { LiveMenuAction } from "./organisms/LiveSessionCard"
export { LiveReportCard } from "./organisms/LiveReportCard"
export { RevisionProgramCard } from "./organisms/RevisionProgramCard"
export { StageProgramHeader } from "./organisms/StageProgramHeader"
export { AdaptProgramSheet } from "./organisms/AdaptProgramSheet"
export { SchoolAssessmentAccordion } from "./organisms/SchoolAssessmentAccordion"
export { LiveEngagementScoreCard } from "./organisms/LiveEngagementScoreCard"
export { ReportChapterSectionCard } from "./organisms/ReportChapterSectionCard"
export { SessionActivitiesCard } from "./organisms/SessionActivitiesCard"
export { MediaPicker } from "./organisms/MediaPicker"
export { MediaDisplay } from "./organisms/MediaDisplay"
export { SchoolAssessmentForm } from "./organisms/SchoolAssessmentForm"
export { AddSchoolAssessmentMedia } from "./organisms/AddSchoolAssessmentMedia"
export { NpsSurvey } from "./organisms/NpsSurvey"
export { ReferralShare } from "./organisms/ReferralShare"
export { ReportConceptsCard } from "./organisms/ReportConceptsCard"
export { ReportSessionTimesCard } from "./organisms/ReportSessionTimesCard"
export { SubscriptionStateCard } from "./organisms/SubscriptionStateCard"
export { SubscriptionNextStepCard } from "./organisms/SubscriptionNextStepCard"
export { ParentHeader } from "./organisms/ParentHeader"
export { NextSessionHero } from "./organisms/NextSessionHero"
export { LastReportCard } from "./organisms/LastReportCard"
export { SessionPickerStep } from "./organisms/SessionPickerStep"
export { TutorPickerStep } from "./organisms/TutorPickerStep"
export { RecoverySlotStep } from "./organisms/RecoverySlotStep"

// Templates — page skeleton + domain section blocks
export { FloatingSlot, PageTemplate, useFloatingBase, useFloatingReserve } from "./templates/PageTemplate"
export { RevisionProgramsSection } from "./templates/RevisionProgramsSection"
export { UpcomingAssessmentsSection } from "./templates/UpcomingAssessmentsSection"
export { GroupedLiveSessionsListSection } from "./templates/GroupedLiveSessionsListSection"
export { RevisionProgramList } from "./templates/RevisionProgramList"
export { LiveReportDetailsTemplate } from "./templates/LiveReportDetailsTemplate"
export { AddSchoolAssessmentModal } from "./templates/AddSchoolAssessmentModal"
export { NpsModal } from "./templates/NpsModal"
export { ReferralModal } from "./templates/ReferralModal"
export { SendTutorMessageModal } from "./templates/SendTutorMessageModal"
export { SubscriptionPortalTemplate } from "./templates/SubscriptionPortalTemplate"
export { PaymentCollectionDayFlow } from "./templates/PaymentCollectionDayFlow"
export { ParentQuickActionsSection } from "./templates/ParentQuickActionsSection"
export { ParentTodoSection } from "./templates/ParentTodoSection"
export { NextSessionSection } from "./templates/NextSessionSection"
export { UpcomingSessionsSection } from "./templates/UpcomingSessionsSection"
export { LastReportSection } from "./templates/LastReportSection"
export { FollowUpSection } from "./templates/FollowUpSection"
export { ParentFlowModal } from "./templates/ParentFlowModal"
export { MoveSessionFlow } from "./templates/MoveSessionFlow"
export type { MoveSessionStep, ContactTutorKind } from "./templates/MoveSessionFlow"
export { ReportLateFlow } from "./templates/ReportLateFlow"
export type { ReportLateStep } from "./templates/ReportLateFlow"
export { TutorPickerModal } from "./templates/TutorPickerModal"
export { ScreenBackground } from "./templates/ScreenBackground"

// Pages
export { ParentHomePage } from "./pages/ParentHomePage"
export { LiveSessionsListPage } from "./pages/LiveSessionsListPage"
export { LiveReportsListPage } from "./pages/LiveReportsListPage"
export { LiveReportDetailsPage } from "./pages/LiveReportDetailsPage"
export { RevisionProgramPage } from "./pages/RevisionProgramPage"
export { StageProgramPage } from "./pages/StageProgramPage"
export { SubscriptionPortalPage } from "./pages/SubscriptionPortalPage"

// Domain-aware component types
export type {
    UISubject,
    UIChapter,
    UISectionedChapter,
    UILiveSession,
    UILiveSessionStatus,
    UILiveSessionGroup,
    UISchoolAssessment,
    UICustomChapter,
    UISchoolAssessmentPreview,
    UICreateSchoolAssessment,
    UIRevisionProgram,
    UIRevisionProgramGroup,
    UILiveSessionReport,
    UILiveSessionReportActivity,
    UIEngagementAlert,
    UIScoreDetailItem,
    UILiveEngagementScore,
    UIActivityLevel,
    UILiveSessionReportChapter,
    UILiveSessionReportDetails,
    UILiveSessionReportConceptGroup,
    UILiveSessionReportTimes,
    UISubscriptionLine,
    UISubscriptionObjective,
    UISubscriptionNextStep,
    UISubscriptionNextStepVariant,
    UISubscriptionCoverageGap,
    UIParentChild,
    UIParentTodoKind,
    UIParentTodo,
    UIParentLastReport,
    UIParentTutorChoice,
    UIParentRecoverySlot,
} from "../ports/primary/ui/types"

// ── Passe de parité visuelle avec la maquette parent (17/09)
export { SectionLabel } from "./atoms/typography/SectionLabel"
export { Logo } from "./atoms/Logo"
export { CardList, CardListRow } from "./molecules/CardList"
export { PARENT_CONTENT_WIDTH } from "./templates/PageTemplate"

// ── Retours du 17/09 au soir : cartes à plat, « Suivi » sobre, actions d'une séance
export { CardListLinkRow } from "./molecules/CardList"
export { RevisionProgramRow } from "./molecules/RevisionProgramRow"
export { SchoolAssessmentRow } from "./molecules/SchoolAssessmentRow"
export { SchoolAssessmentDetailModal } from "./templates/SchoolAssessmentDetailModal"
export { LiveSessionActionsSheet } from "./templates/LiveSessionActionsSheet"
export type { LiveSessionAction } from "./templates/LiveSessionActionsSheet"

// ── Étape 2 : « Mettre en pause l'abonnement »
export { PauseDatesStep } from "./organisms/PauseDatesStep"
export { PauseRecapStep } from "./organisms/PauseRecapStep"
export { PauseSubscriptionFlow, PAUSE_SUBMIT_ERROR_FALLBACK } from "./templates/PauseSubscriptionFlow"
export type { PauseSubscriptionStep } from "./templates/PauseSubscriptionFlow"
export type { UIPauseReason, UIPauseRecap } from "../ports/primary/ui/types"
// ── Tableau de parrainage (le cœur du header parent)
export { ReferralStatTile } from "./molecules/ReferralStatTile"
export { ReferralHowItWorksStep } from "./molecules/ReferralHowItWorksStep"
export { ReferralStatsCard } from "./organisms/ReferralStatsCard"
export { ReferralHowItWorks } from "./organisms/ReferralHowItWorks"
export { ReferralTermsCard } from "./organisms/ReferralTermsCard"
export { ReferralDashboardPage } from "./pages/ReferralDashboardPage"
export type { UIReferralDashboard, UIReferralStats } from "../ports/primary/ui/types"
// ── Étape 2 : « Ajouter un cours ponctuel » (heures supplémentaires)
export { TimeSlotGrid } from "./organisms/TimeSlotGrid"
export { ExtraHoursParametersStep } from "./organisms/ExtraHoursParametersStep"
export { ExtraHoursDatesStep } from "./organisms/ExtraHoursDatesStep"
export { ExtraHoursRecapStep } from "./organisms/ExtraHoursRecapStep"
export { ExtraHoursFlow } from "./templates/ExtraHoursFlow"
export type { ExtraHoursStep } from "./templates/ExtraHoursFlow"
export type {
    UIExtraHoursFormat,
    UIExtraHoursFormatOption,
    UIExtraHoursDeadlineOption,
    UITimeSlot,
    UITimeSlotDay,
    UIExtraHoursDraft,
} from "../ports/primary/ui/types"
// ── Parcours « Résilier l'abonnement » (cinq étapes)
export { CancelFeesCard } from "./molecules/CancelFeesCard"
export { CancelSummaryCard } from "./molecules/CancelSummaryCard"
export { CancelAlternativeCard } from "./organisms/CancelAlternativeCard"
export { CancelPaymentMethod } from "./organisms/CancelPaymentMethod"
export { CancelFlowStep, CANCEL_FLOW_STEPS } from "./templates/CancelFlowStep"
export { CancelReasonPage } from "./pages/CancelReasonPage"
export { CancelAlternativesPage } from "./pages/CancelAlternativesPage"
export { CancelFeesPage } from "./pages/CancelFeesPage"
export { CancelPaymentPage } from "./pages/CancelPaymentPage"
export { CancelGoodbyePage } from "./pages/CancelGoodbyePage"
export type {
    UICancelReason,
    UICancelAlternativeAction,
    UICancelAlternative,
    UICancelFees,
    UICancelSummaryRow,
    UICancelPaymentMethod,
    UICancelPaymentStatus,
    UICancelPaymentNotice,
} from "../ports/primary/ui/types"
// ── Étape 2 : l'onglet « Activités » de l'espace parent
export { ActivityRow } from "./molecules/ActivityRow"
export { ActivitySearchBar } from "./molecules/ActivitySearchBar"
export { SelectionActionBar } from "./molecules/SelectionActionBar"
export { SelectionHeaderRow } from "./molecules/SelectionHeaderRow"
export { EmptyState } from "./molecules/EmptyState"
export { ActivitiesPage } from "./pages/ActivitiesPage"
export { ArchivedActivitiesPage } from "./pages/ArchivedActivitiesPage"
export { RealizationsPage } from "./pages/RealizationsPage"
export type { UIActivity, UIActivityStatus } from "../ports/primary/ui/types"
// ── Parcours « Activer la suite » (étape 2)
export { ActivationStudentHeader } from "./molecules/ActivationStudentHeader"
export { ActivationProductCard } from "./molecules/ActivationProductCard"
export { ActivationLandingPage, ACTIVATION_CONTENT_WIDTH } from "./pages/ActivationLandingPage"
export type {
    UIActivationLandingState,
    UIActivationProduct,
    UIActivationNotice,
    UIActivationOutcome,
    UIActivationChoice,
    UIActivationAllocationRow,
    UIActivationPriceSummary,
} from "../ports/primary/ui/types"
export { ActivationChoiceSection } from "./organisms/ActivationChoiceSection"
export { ActivationPickerSection } from "./organisms/ActivationPickerSection"
export { ActivationResumeDateSection } from "./organisms/ActivationResumeDateSection"
export { ActivationAllocationSection } from "./organisms/ActivationAllocationSection"
export { ActivationCommitmentSection } from "./organisms/ActivationCommitmentSection"
export { ActivationPriceSummary } from "./organisms/ActivationPriceSummary"
export { ActivationComposerTemplate } from "./templates/ActivationComposerTemplate"
export { ActivationComposerPage } from "./pages/ActivationComposerPage"
// ── Portail Abonnement câblé (lot 3) : la feuille de bascule de profil et le geste contrôlé
export { ProfileSwitcherSheet } from "./molecules/ProfileSwitcherSheet"
export type { ProfileSwitcherSheetProps } from "./molecules/ProfileSwitcherSheet"
export type { PaymentCollectionDayFlowProps, PaymentCollectionDayStep } from "./templates/PaymentCollectionDayFlow"
export type { SubscriptionPortalPageProps } from "./pages/SubscriptionPortalPage"
// ── Activités : la fiche d'une activité, et « Générer une activité » (étape 2, espace parent)
export { ChoiceChip } from "./atoms/ChoiceChip"
export { ActivityStatusBadge } from "./molecules/ActivityStatusBadge"
export { ActivityMediaList } from "./organisms/ActivityMediaList"
export { ActivityDetailsHeader } from "./organisms/ActivityDetailsHeader"
export { ActivityStatement } from "./organisms/ActivityStatement"
export { ActivityWork } from "./organisms/ActivityWork"
export { ActivityCorrectionPoint } from "./molecules/ActivityCorrectionPoint"
export { ActivityCorrectionPoints } from "./organisms/ActivityCorrectionPoints"
export { ActivityCorrectionMarker } from "./molecules/ActivityCorrectionMarker"
export { ActivityCorrectedCopy } from "./organisms/ActivityCorrectedCopy"
export { ActivityAnalysisNotice } from "./molecules/ActivityAnalysisNotice"
export { GeneratedActivityCard } from "./molecules/GeneratedActivityCard"
export { ActivityGenerationForm } from "./organisms/ActivityGenerationForm"
export { ActivityFileDropZone } from "./organisms/ActivityFileDropZone"
export { GeneratedActivityResults } from "./organisms/GeneratedActivityResults"
export { ActivityDetailsModal } from "./templates/ActivityDetailsModal"
export { GenerateActivityFlow } from "./templates/GenerateActivityFlow"
// ── Markdown + LaTeX : l'énoncé rendu comme l'élève le voit
export { RichText } from "./molecules/RichText"
export type {
    UIActivityMedia,
    UIActivityStatement,
    UIActivityCorrectionAnchor,
    UIActivityCorrectionPoint,
    UIActivityCorrectionPointType,
    UIActivityWork,
    UIActivityDetails,
    UIActivityDisplayMode,
    UIActivityDetailsAction,
    UIActivityDetailsDialog,
    UIActivityGenerationOption,
    UIActivitySkillPart,
    UIActivityGenerationDraft,
    UIActivityGenerationCatalogs,
    UIActivityGenerationField,
    UIActivityFile,
    UIActivityAnalysisStatus,
    UIActivityAnalysis,
    UIGeneratedActivityCreation,
    UIGeneratedActivity,
    UIActivityGenerationStep,
} from "../ports/primary/ui/types"
// ── « Planifier un rattrapage » : le parcours DS ouvert depuis « À faire » de l'accueil parent
export { AbsencePickerStep } from "./organisms/AbsencePickerStep"
export { RecoverAbsenceFlow } from "./templates/RecoverAbsenceFlow"
export type { RecoverAbsenceStep } from "./templates/RecoverAbsenceFlow"
export type { UIParentRecoveryAbsence } from "../ports/primary/ui/types"
// ── « Garder son tuteur » : le choix ouvert depuis « À faire » quand un tuteur arrête un créneau
export { StayWithTutorStep } from "./organisms/StayWithTutorStep"
export { StayWithTutorFlow } from "./templates/StayWithTutorFlow"
export type { StayWithTutorFlowStep } from "./templates/StayWithTutorFlow"
export type {
    UITutorSlotMove,
    UITutorSlotMoveOption,
    UITutorSlotMoveChoice,
    UITutorSlotMoveOutcome,
} from "../ports/primary/ui/types"
// ── « Modifier mes créneaux » : le parcours ouvert depuis Abonnement › Gestion
export { WeekSlotChip } from "./molecules/WeekSlotChip"
export { WeekSlotGrid } from "./organisms/WeekSlotGrid"
export { SlotChangeCoursesStep } from "./organisms/SlotChangeCoursesStep"
export { SlotChangeSeatsStep } from "./organisms/SlotChangeSeatsStep"
export { SlotChangeFallbackStep } from "./organisms/SlotChangeFallbackStep"
export { SlotChangeFlow } from "./templates/SlotChangeFlow"
export { SlotChangePendingDetail } from "./molecules/SlotChangePendingDetail"
export { ParentRequestRow } from "./molecules/ParentRequestRow"
export { ParentRequestsSection } from "./templates/ParentRequestsSection"
export type { SlotChangeFlowStep } from "./templates/SlotChangeFlow"
export type {
    UISlotChangeCourse,
    UISlotChangeSeat,
    UISlotChangeTutor,
    UISlotChangeGrid,
    UISlotChangeGridDay,
    UISlotChangeGridSlot,
    UISlotChangeFallback,
    UISlotChangeFallbackChoice,
    UISlotChangeOptions,
    UISlotChangeOutcome,
    UISlotChangePending,
    UIParentRequest,
} from "../ports/primary/ui/types"
// ── Retours du 28/09 sur l'accueil parent : le parrainage passe du cœur du header à un bandeau
export { ReferralInviteBanner } from "./molecules/ReferralInviteBanner"
// ── Le stage de vacances en vente sur l'accueil parent : la carte d'événement du livre
export { StageOfferBanner } from "./molecules/StageOfferBanner"
export type { UIParentStageOffer } from "../ports/primary/ui/types"
// ── Une seule anatomie de rapport : l'en-tête commun à « Dernier bilan » et à l'onglet Rapports
export { LiveReportHeader } from "./molecules/LiveReportHeader"

// Connexion — les écrans d'avant connexion (maquettes ; le câblage suit derrière un drapeau).
export { CodeInput } from "./atoms/CodeInput"
export { AuthTemplate } from "./templates/AuthTemplate"
export { textInputAutofill } from "./atoms/TextInput"
export { ErrorText } from "./atoms/typography/ErrorText"
export { AuthHeading } from "./molecules/AuthHeading"
export { ReadOnlyField } from "./molecules/ReadOnlyField"
export { LoginTerms } from "./molecules/LoginTerms"
export { LoginStartStep } from "./organisms/LoginStartStep"
export { LoginPasswordStep } from "./organisms/LoginPasswordStep"
export { LoginCodeStep } from "./organisms/LoginCodeStep"
export { LoginPage } from "./pages/LoginPage"

// Connexion — lien-mots-de-passe : l'ouverture du lien « Me connecter », « Mot de passe oublié ? », « Nouveau mot de passe »
export { LoginLinkPage } from "./pages/LoginLinkPage"
export { ImpersonatePage } from "./pages/ImpersonatePage"
export type { UILoginCodeChannel, UILoginLinkStatus, UILoginMode } from "../ports/primary/ui/types"
export { ForgotPasswordPage } from "./pages/ForgotPasswordPage"
export { ResetPasswordPage } from "./pages/ResetPasswordPage"

// Connexion — qui-utilise-masteur : le choix du compte.
export { AccountPickerTile } from "./molecules/AccountPickerTile"
export { AccountPickerGroup } from "./organisms/AccountPickerGroup"
export { AccountPickerPage } from "./pages/AccountPickerPage"
export { AccountChoicePage } from "./pages/AccountChoicePage"
export type {
    UIStoredAccount,
    UIStoredAccountFamily,
    UIStoredAccountGroup,
    UIAccountPickerTarget,
    UIAccountPickerRemoval,
} from "../ports/primary/ui/types"
export { WaitingText } from "./atoms/WaitingText"
export { StepStage } from "./atoms/StepStage"

export { TimezonePicker } from "./organisms/TimezonePicker"
export { AccountTimezonePage } from "./pages/AccountTimezonePage"
export type { UITimezoneOption } from "../ports/primary/ui/types"
